import { evaluate, lookupMathFunction, referencesY } from "./evaluator";
import type { ASTNode } from "./parser";

type CallExpressionNode = Extract<ASTNode, { type: "CallExpression" }>;

/**
 * Result of validating a parsed formula.
 *
 * On success, `constants` maps each windowed CallExpression node to its
 * pre-evaluated non-first arguments (`[windowSeconds, polynomial]` for savgol,
 * `[n]` for deriv), so series evaluation skips re-walking constant subtrees.
 *
 * Keys are node identities. Each parse yields a fresh tree, so identity is
 * stable for the lifetime of one `compileFormula` result.
 */
export type ValidationResult =
  | {
      ok: true;
      constants: ReadonlyMap<CallExpressionNode, readonly number[]>;
    }
  | { ok: false; error: string };

const SAVGOL_MIN_POLYNOMIAL = 1;
const SAVGOL_MAX_POLYNOMIAL = 5;

/**
 * Validate a parsed formula AST. Checks, in order:
 *   - Every callee resolves against the registry (`MATH_FUNCTIONS`).
 *   - Each windowed call's arity, constant args, and ranges (savgol window > 0;
 *     savgol polynomial in [1, 5]; deriv n in {1, 2}).
 *   - Every windowed call's first argument references `y`.
 *   - A dry-run with `y = 1` surfaces unbound identifiers and unknown scalar
 *     functions outside any windowed call.
 *
 * On success, returns the pre-evaluated non-first arguments per windowed call
 * so series evaluation runs the kernel without re-evaluating constants.
 */
export function validateAst(ast: ASTNode): ValidationResult {
  const constants = new Map<CallExpressionNode, readonly number[]>();
  try {
    const callSites: CallExpressionNode[] = [];
    collectCallSites(ast, callSites);

    // Validate every call site (function exists; windowed calls also get
    // arity + constant args + range) and record the windowed sites in tree
    // order, so the dry-run pass below reuses this classification instead of
    // re-resolving each callee. Scalar arity is intentionally unchecked:
    // `sin(1, 2, 3)` dispatches to `Math.sin`, which ignores the extras.
    const windowedSites: CallExpressionNode[] = [];
    for (const node of callSites) {
      const entry = lookupMathFunction(node.callee);
      if (!entry) {
        throw new Error(`Undefined function: '${node.callee}'`);
      }
      if (entry.kind === "windowed") {
        constants.set(node, validateWindowedCall(node));
        windowedSites.push(node);
      }
    }

    // `collectCallSites` yields outer→inner order (pre-order DFS), so iterate
    // in reverse: each outer site's dry-run then sees its inner windowed sites
    // already stubbed.
    const stubCache = new Map<ASTNode, readonly number[]>();
    for (let i = windowedSites.length - 1; i >= 0; i--) {
      const node = windowedSites[i];
      // Surface unbound identifiers / unknown functions in the first argument
      // before the y-reference check, so `savgol(unknown_var, 0.5, 3)` reports
      // "Undefined variable" rather than the vaguer "does not reference y".
      evaluate(node.arguments[0], { y: 1 }, stubCache, 0);
      if (!referencesY(node.arguments[0])) {
        throw new Error(
          `Formula uses windowed function "${node.callee}" with a first argument that does not reference y`,
        );
      }
      stubCache.set(node, [1]);
    }

    // Final dry-run of the full AST: catches identifiers/functions outside
    // any windowed call (e.g. `unknown + savgol(y, 0.5, 3)`).
    evaluate(ast, { y: 1 }, stubCache, 0);
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
  return { ok: true, constants };
}

function collectCallSites(node: ASTNode, out: CallExpressionNode[]): void {
  switch (node.type) {
    case "Literal":
    case "Identifier":
      return;
    case "BinaryExpression":
      collectCallSites(node.left, out);
      collectCallSites(node.right, out);
      return;
    case "UnaryExpression":
      collectCallSites(node.argument, out);
      return;
    case "CallExpression":
      out.push(node);
      for (const arg of node.arguments) {
        collectCallSites(arg, out);
      }
      return;
  }
}

function validateWindowedCall(node: CallExpressionNode): readonly number[] {
  if (node.callee === "savgol") {
    return validateSavgol(node);
  }
  if (node.callee === "deriv") {
    return validateDeriv(node);
  }
  // Unreachable today (callers filter on `entry.kind === "windowed"`); guards
  // against a new windowed entry in `MATH_FUNCTIONS` lacking a branch here.
  throw new Error(`Missing validator for windowed function '${node.callee}'`);
}

function validateSavgol(node: CallExpressionNode): readonly number[] {
  if (node.arguments.length !== 3) {
    throw new Error(
      "savgol(y, windowSeconds, polynomial) requires 3 arguments",
    );
  }
  const windowSeconds = evaluateConstantArg(node.arguments[1], "savgol window");
  const polynomial = evaluateConstantArg(node.arguments[2], "savgol order");
  if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) {
    throw new Error(`savgol window must be > 0 seconds (got ${windowSeconds})`);
  }
  if (!Number.isInteger(polynomial) || polynomial < SAVGOL_MIN_POLYNOMIAL) {
    throw new Error(
      "savgol order must be a positive integer (≥ 1); note this diverges from scipy.savgol_filter, which accepts polyorder=0",
    );
  }
  if (polynomial > SAVGOL_MAX_POLYNOMIAL) {
    throw new Error(
      `savgol order must be <= ${SAVGOL_MAX_POLYNOMIAL} (got ${polynomial}); higher polynomial degrees are not recommended by the underlying sgg library`,
    );
  }
  return [windowSeconds, polynomial];
}

function validateDeriv(node: CallExpressionNode): readonly number[] {
  if (node.arguments.length < 1 || node.arguments.length > 2) {
    throw new Error("deriv(y, n?) requires 1 or 2 arguments");
  }
  const n =
    node.arguments.length === 2
      ? evaluateConstantArg(node.arguments[1], "deriv n")
      : 1;
  if (n !== 1 && n !== 2) {
    throw new Error(
      `deriv n must be 1 (first derivative) or 2 (second derivative); got ${n}`,
    );
  }
  return [n];
}

/**
 * Evaluate an AST node expected to reduce to a constant. Any error from
 * {@link evaluate} — unbound identifier, unknown function, misplaced windowed
 * call, operator error — is re-wrapped as `"<label> must be a constant:
 * <cause>"`, giving one diagnostic without matching on the underlying message.
 */
function evaluateConstantArg(node: ASTNode, label: string): number {
  try {
    return evaluate(node, {});
  } catch (e) {
    const cause = e instanceof Error ? e.message : String(e);
    throw new Error(`${label} must be a constant: ${cause}`);
  }
}
